/* eslint-disable @nx/enforce-module-boundaries */
/* eslint-disable no-unsafe-optional-chaining */
import { inject, Injectable } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { Preferences } from "@capacitor/preferences";
import { UserDto, UserService } from "@malirang/api-client";
import { PARENT_ACCESS_TOKEN_KEY } from "@malirang/common";
import { BaseStore } from "@malirang/front-share";
import { toObservableSignal } from "ngxtension/to-observable-signal";
import { lastValueFrom, map } from "rxjs";
import { KeywordStore } from "../components/filter/filter-wrapper/store/keyword.store";
import { MypageStore } from "../pages/my-page/store/my-page.store";
import { IapService } from "../services/iap.service";
import { ChildStore } from "./child.store";
import {
    ConnectEventSourceOptions,
    NotificationStore,
} from "./notification.store";

type State = {
    user: UserDto | null;
};

@Injectable({ providedIn: "root" })
export class UserStore extends BaseStore<State> {
    private readonly router = inject(Router);
    private readonly userService = inject(UserService);
    private readonly childStore = inject(ChildStore);
    private readonly keywordStore = inject(KeywordStore);
    private readonly notificationStore = inject(NotificationStore);
    private readonly MypageStore = inject(MypageStore);
    private readonly iapService = inject(IapService);

    user = toObservableSignal<UserDto | null>(
        this.state$.pipe(map((state) => state.user)),
    );
    isAuth = toSignal<boolean>(this.state$.pipe(map((state) => !!state.user)));

    constructor() {
        super({
            user: null,
        });
    }

    setUser(user: UserDto) {
        this.updateState({ user });
    }

    async fetchUser(): Promise<UserDto | undefined> {
        try {
            const accessToken = (
                await Preferences?.get({ key: PARENT_ACCESS_TOKEN_KEY })
            ).value;

            if (!accessToken) return;

            const user = await lastValueFrom(
                this.userService.getUserControllerGetMe(),
            );

            if (!user) {
                await Preferences?.remove({
                    key: PARENT_ACCESS_TOKEN_KEY,
                });
                this.updateState({ user: null });
                return;
            }

            this.setUser(user);
            this.notificationStore.fetchState();
            this.childStore.fetchAll();
            await this.notificationStore.updateFCMToken(user);
            await this.iapService.identifyUser(user.id);
            await this.notificationStore.connectSSE({
                userId: user.id,
                token: accessToken,
            } as ConnectEventSourceOptions);

            return user;
        } catch (error) {
            this.updateState({ user: null });
            console.error(error);
            await Preferences?.remove({ key: PARENT_ACCESS_TOKEN_KEY });
            return undefined;
        }
    }

    async logout() {
        this.updateState({ user: null });
        await this.childStore.clear();
        this.keywordStore.clear();
        await this.notificationStore.clear();
        this.notificationStore.closeSSE();
        this.MypageStore.clear();
        await Preferences?.remove({ key: PARENT_ACCESS_TOKEN_KEY });
        this.router.navigate(["/loading"]);
    }
}
